interface TaskGraphItem {
  id?: string;
  status: string;
  predecessor_ids: string[];
  created_at?: number;
}

export interface TaskDependencyLevel<T extends TaskGraphItem> {
  level: number;
  tasks: T[];
}

export interface TaskDependencyPosition<T extends TaskGraphItem> {
  row: number;
  column: number;
  ancestorId: string;
  task: T;
}

export interface TaskDependencyRow<T extends TaskGraphItem> {
  row: number;
  items: TaskDependencyPosition<T>[];
}

export interface TaskDependencyLayout<T extends TaskGraphItem> {
  rows: TaskDependencyRow<T>[];
  regions: TaskDependencyRegion<T>[];
  columnCount: number;
}

export interface TaskDependencyGroup<T extends TaskGraphItem> {
  groupId: string;
  row: number;
  tasks: T[];
}

export interface TaskDependencyRegion<T extends TaskGraphItem> {
  ancestorId: string;
  ancestor: T;
  startColumn: number;
  columnCount: number;
  groups: TaskDependencyGroup<T>[];
}

export function getReadyTasks<T extends TaskGraphItem>(tasks: T[]): T[] {
  const taskMap = new Map(tasks.filter((task) => task.id).map((task) => [task.id!, task]));
  return tasks.filter(
    (task) =>
      task.status !== "done" &&
      task.status !== "discarded" &&
      (task.predecessor_ids || []).every(
        (predecessorId) =>
          ["done", "discarded"].includes(
            taskMap.get(predecessorId)?.status || ""
          )
      )
  );
}

export function getFocusedReadyTask<T extends TaskGraphItem>(tasks: T[]): T | null {
  const statusOrder: Record<string, number> = {
    doing: 0,
    blocked: 1,
    todo: 2,
    review: 3,
  };
  return (
    getReadyTasks(tasks).sort(
      (prev, next) =>
        (statusOrder[prev.status] ?? 9) - (statusOrder[next.status] ?? 9) ||
        (prev.created_at || 0) - (next.created_at || 0)
    )[0] || null
  );
}

export function groupTasksByDependencyLevel<T extends TaskGraphItem>(
  tasks: T[]
): TaskDependencyLevel<T>[] {
  const taskMap = new Map(
    tasks.filter((task) => task.id).map((task) => [task.id!, task])
  );
  const depthCache = new Map<string, number>();
  const visiting = new Set<string>();

  const getDepth = (task: T): number => {
    if (!task.id) return 0;
    const cachedDepth = depthCache.get(task.id);
    if (cachedDepth !== undefined) return cachedDepth;
    if (visiting.has(task.id)) return 0;

    visiting.add(task.id);
    const predecessorDepths = (task.predecessor_ids || [])
      .map((predecessorId) => taskMap.get(predecessorId))
      .filter((predecessor): predecessor is T => Boolean(predecessor))
      .map(getDepth);
    visiting.delete(task.id);

    const depth = predecessorDepths.length
      ? Math.max(...predecessorDepths) + 1
      : 0;
    depthCache.set(task.id, depth);
    return depth;
  };

  const levelMap = new Map<number, T[]>();
  tasks.forEach((task) => {
    const depth = getDepth(task);
    levelMap.set(depth, [...(levelMap.get(depth) || []), task]);
  });

  return Array.from(levelMap.entries())
    .sort(([previousDepth], [nextDepth]) => previousDepth - nextDepth)
    .map(([depth, levelTasks]) => ({
      level: depth + 1,
      tasks: [...levelTasks].sort(
        (previous, next) =>
          (previous.created_at || 0) - (next.created_at || 0) ||
          String(previous.id || "").localeCompare(String(next.id || ""))
      ),
    }));
}

export function layoutTasksByDependencyRows<T extends TaskGraphItem>(
  tasks: T[]
): TaskDependencyLayout<T> {
  const grouped = groupTasksByDependencyLevel(tasks);
  const positionById = new Map<string, TaskDependencyPosition<T>>();
  const localRows: TaskDependencyRow<T>[] = [];
  const regionById = new Map<string, TaskDependencyRegion<T>>();

  grouped.forEach((group, row) => {
    const items: TaskDependencyPosition<T>[] = [];

    group.tasks.forEach((task, taskIndex) => {
      const primaryPredecessor = (task.predecessor_ids || [])
        .map((predecessorId) => positionById.get(predecessorId))
        .find(
          (position): position is TaskDependencyPosition<T> => Boolean(position)
        );
      const ancestorId =
        primaryPredecessor?.ancestorId ||
        task.id ||
        `root:${row}:${taskIndex}`;

      if (!regionById.has(ancestorId)) {
        regionById.set(ancestorId, {
          ancestorId,
          ancestor: task,
          startColumn: regionById.size,
          columnCount: 1,
          groups: [],
        });
      }
      const region = regionById.get(ancestorId)!;
      const groupId = `${ancestorId}:${row}`;
      const dependencyGroup = region.groups.find(
        (candidate) => candidate.groupId === groupId
      );
      if (dependencyGroup) {
        dependencyGroup.tasks.push(task);
      } else {
        region.groups.push({
          groupId,
          row,
          tasks: [task],
        });
      }

      const position = {
        row,
        column: region.startColumn,
        ancestorId,
        task,
      };
      if (task.id) positionById.set(task.id, position);
      items.push(position);
    });
    localRows.push({ row, items });
  });

  const regions = Array.from(regionById.values());

  return {
    rows: localRows,
    regions,
    columnCount: regions.length,
  };
}
